import type { BmiClass } from "../../../convex/lib/engine";
import { cn } from "@/lib/cn";

interface BmiCardProps {
  readonly bmi: number;
  readonly classification: BmiClass;
}

const CLASS_LABELS: Record<BmiClass, string> = {
  underweight: "Underweight",
  healthy: "Healthy",
  overweight: "Overweight",
  obesity: "Obesity",
};

/** Display scale for the zone bar; the marker clamps into this window. */
const SCALE_MIN = 15;
const SCALE_MAX = 40;

const ZONES = [
  { key: "underweight", from: SCALE_MIN, to: 18.5, className: "bg-sky-300" },
  { key: "healthy", from: 18.5, to: 25, className: "bg-accent" },
  { key: "overweight", from: 25, to: 30, className: "bg-warning" },
  { key: "obesity", from: 30, to: SCALE_MAX, className: "bg-danger" },
] as const;

const SCALE_SPAN = SCALE_MAX - SCALE_MIN;

function zoneWidthPercent(from: number, to: number): number {
  return ((to - from) / SCALE_SPAN) * 100;
}

function markerPercent(bmi: number): number {
  const clamped = Math.min(SCALE_MAX, Math.max(SCALE_MIN, bmi));
  return ((clamped - SCALE_MIN) / SCALE_SPAN) * 100;
}

/** Compact BMI readout with a four-zone classification bar and screening disclaimer. */
export function BmiCard({ bmi, classification }: BmiCardProps) {
  return (
    <div
      data-testid="bmi-card"
      className="flex w-full flex-col gap-2 rounded-2xl border border-border bg-surface p-4"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
          Your BMI
        </span>
        <span className="text-sm text-text">
          <span className="text-lg font-bold tabular-nums">{bmi.toFixed(1)}</span>{" "}
          <span
            className={cn(
              "font-semibold",
              classification === "healthy" ? "text-accent" : "text-warning",
            )}
          >
            {CLASS_LABELS[classification]}
          </span>
        </span>
      </div>
      <div
        role="img"
        aria-label={`BMI ${bmi.toFixed(1)}, classified as ${CLASS_LABELS[classification]}`}
        className="relative"
      >
        <div className="flex h-2.5 w-full overflow-hidden rounded-full">
          {ZONES.map((zone) => (
            <div
              key={zone.key}
              className={zone.className}
              style={{ width: `${zoneWidthPercent(zone.from, zone.to)}%` }}
            />
          ))}
        </div>
        <div
          className="absolute -top-1 h-[18px] w-[18px] -translate-x-1/2 rounded-full border-2 border-surface bg-text shadow-sm"
          style={{ left: `${markerPercent(bmi)}%` }}
          aria-hidden="true"
        />
      </div>
      <div className="flex justify-between text-[10px] font-medium text-text-muted" aria-hidden="true">
        <span>18.5</span>
        <span>25</span>
        <span>30</span>
      </div>
      <p className="text-[11px] leading-relaxed text-text-muted">
        BMI is a general screening measure — it doesn't directly measure body fat, muscle, or
        individual health.
      </p>
    </div>
  );
}
